import { useEffect, useState } from "react";
import { ApiError } from "../api";

/** Minimal data-fetching hook: enough for a prototype, no cache to reason about. */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[]) {
  const [state, setState] = useState<{ data?: T; error?: string; loading: boolean }>({
    loading: true,
  });
  useEffect(() => {
    let live = true;
    setState({ loading: true });
    load()
      .then((data) => live && setState({ data, loading: false }))
      .catch((e: unknown) =>
        live &&
        setState({
          loading: false,
          error: e instanceof ApiError ? e.message : String(e),
        })
      );
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return state;
}

export function Spinner() {
  return <div className="spinner" aria-label="загрузка" />;
}

export function Notice({ children }: { children: React.ReactNode }) {
  return <div className="notice">{children}</div>;
}

export function Section({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <div className="section">
      {title && <h2>{title}</h2>}
      {children}
    </div>
  );
}

const MONTHS = [
  "янв", "фев", "мар", "апр", "мая", "июн",
  "июл", "авг", "сен", "окт", "ноя", "дек",
];

export function ruDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export function ruMonth(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** Thin-space thousands, so 14318 reads as 14 318 everywhere it appears. */
export const num = (n: number) => n.toLocaleString("ru");

/** Russian needs three plural forms, and "5 сообщения" reads as broken. */
export function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${num(n)} ${one}`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${num(n)} ${few}`;
  return `${num(n)} ${many}`;
}

export const msgs = (n: number) => plural(n, "сообщение", "сообщения", "сообщений");
export const people = (n: number) => plural(n, "участник", "участника", "участников");
export const topicsStarted = (n: number) => plural(n, "тема", "темы", "тем");
export const discussions = (n: number) =>
  plural(n, "обсуждение", "обсуждения", "обсуждений");
